import Backbone from 'backbone';

import Config from '../../config'
import Model from '../model';

import { fmt, unixts, parseYYYYMMDD } from '../../utils/date';





export default Model.extend( {

    endpoint: 'fields', responseAt: 'custom_field',
    
    defaults() {
        return {
            // "id" must be specified here in order
            // to make Epoxy's "toggle:not(id)" binding filter work.
            // (see customField.form.handlebars and "format"
            // field for example).
            id: undefined,
            cluster: null,
            table_id: undefined, multiselect: undefined, allow_add_num: undefined,
            name: '',
            format: 'string',
            group_name: undefined,
            possible_values: [],
            regexp: undefined,
            min_length: undefined,
            max_length: undefined,
            is_required: 'f',
            default_value: undefined,
            visible: 't',
            is_for_all: 'f',
            bindings: [],
            min_count: 1,
            max_count: 1,
            min_size: undefined,
            max_size: undefined,
            auto_apply_sticker: 'f',
            subfields: [],
            file_semantics: ['PHOTO'],
            sticker_ids: null, __all_stickers_available: undefined,
        }
    },
    
    computeds: {
        
        // make copy of "regexp" property
        // with start and end "/"
        regexp_: {
            
            set(value) {
                if ('string' !== typeof value)
                    return { regexp: value };
                    
                if ('' === value)
                    return { regexp: '' };
                    
                return { regexp: `/${value}/` };
            },
            
            get() {
                let value = this.get( 'regexp' );
                // "/value/"
                if ('string' === typeof value)
                    // correct for empty string too
                    value = value.substring( 1, value.length - 1 ); 
                return value;
            }
        },
        
        // treat "0" for minlength and maxlength as no value
        min_length_: {
            
            set(value) {
                if ('' === value)
                    return { min_length: 0 };
                return { min_length: value }; 
            },
            
            get() {
                let value = this.get( 'min_length' );
                if ('0' === value)
                    return undefined; 
                if (0 === value) 
                    return undefined;
                    
                return value;
            }
            
        },

        // treat "0" for minlength and maxlength as no value
        max_length_: {
            
            set(value) {
                if ('' === value)
                    return { max_length: 0 };
                return { max_length: value }; 
            },
            
            get() {
                let value = this.get( 'max_length' );
                if ('0' === value)
                    return undefined; 
                if (0 === value) 
                    return undefined;
                    
                return value;
            }
            
        },
        
        
        // treat list of strings as string with "\n"s
        // at UI level
        possible_values_: {

            set(value) {
                const newPossibleValues = value.split( '\n' );

                const defaultValue = this.get( 'default_value' );
                if (!defaultValue)
                    return { possible_values: newPossibleValues }; 

                // default value hasn't changed in possible values list
                if (~newPossibleValues.indexOf( defaultValue ))
                    return { possible_values: newPossibleValues }; 

                const oldPossibleValues = this.get( 'possible_values' ) || [];
                if (newPossibleValues.length !== oldPossibleValues.length) 
                    return { possible_values: newPossibleValues, default_value: '' }; 
                
                const oldDefaultValueIndex = oldPossibleValues.indexOf( defaultValue );
                if (oldDefaultValueIndex < 0)
                    return { possible_values: newPossibleValues, default_value: '' }; 

                const changedItems = newPossibleValues
                    .filter( (value, index ) => value !== oldPossibleValues[index] );
                // no changes or more than one row has been changed:
                // cannot find match for old default value in changed rows
                if (!changedItems.length || changedItems.length > 1)
                    return { possible_values: newPossibleValues, default_value: '' }; 

                // * lengths are equal
                // * one change 
                // * defval is in old list
                // * defval is not in new list
                // -> take new list in position of change
                return { possible_values: newPossibleValues, default_value: changedItems[0] }; 
            },
            
            get() {
                let value = this.get( 'possible_values' );
                if (value instanceof Array)
                    value = value.join( '\n' );
                    
                return value;
            }
            
        },

        default_datetime: {

            set(value) {
                if (this.get( 'format') !== 'date')
                    return {};

                return { default_value: value };
            },

            get() {
                if (this.get( 'format') !== 'date')
                    return null;

                const value = this.get( 'default_value' );
                if (!value)
                    return null;

                return value;
            }

        },

        // for converting timestamp (as in binder) to string (as in model/server)
        default_date: {

            set(value) {
                if (this.get( 'format') !== 'date-only')
                    return {};

                return { default_value: fmt( value, 'YYYY-MM-DD' ) };
            },

            get() {
                if (this.get( 'format') !== 'date-only')
                    return null;

                const value = this.get( 'default_value' );
                if (!value)
                    return null;

                // no need in fake "00:00:00" string
                // because this timestamp will be converted to date string next
                return unixts( parseYYYYMMDD( value ) );
            }

        },

        default_possible_value: {

            set(value) {
                return { default_value: value };
            },

            get() {
                return {
                    selected: this.get( 'default_value' ),
                    options: this.get( 'possible_values' )
                };
            },
        },

    },
    
    customParser(payload) {
        if (payload.subfields instanceof Array)
            payload.subfields = payload.subfields.map( 
                subfieldPayload => this.customParser( subfieldPayload ) );

        if ('string' === typeof payload.possible_values)
            payload.possible_values = payload.possible_values.split( '\n' );
        if ('' === payload.regexp)
            payload.regexp = undefined;
        if (payload.order)
            payload.order = +payload.order;

        const { news_type_ids, ...json } = payload;
        return { ...json };
    },

    clone() {
        const clonify = ({ subfields, ...json }) => ({
            ...json,
            subfields: subfields.map( clonify )
        });

        const Class = this.constructor;
        return new Class( clonify( this.toJSON() ) );
    },

    diff(another, options = {}) {
        const diff = Model.prototype.diff.call( this, another, options )
            .filter( ({ attrName }) => attrName !== 'bindings');

        const oldBindings = another.get( 'bindings' );
        const newBindings = this.get( 'bindings' );

        const normalizeBindings = bindings =>
            bindings
                .map( normalizeBinding )
                .sort( (bindingA, bindingB) => bindingA.type.id - bindingB.type.id );

        const originalBindings = normalizeBindings( oldBindings );
        const fieldBindings = normalizeBindings( newBindings );

        if ( JSON.stringify( originalBindings ) !== JSON.stringify( fieldBindings ))
            diff.push( {
                attrName: 'bindings',
                oldValue: oldBindings, newValue: newBindings
            } );

        const oldSubfields = another.get( 'subfields' );
        const newSubfields = this.get( 'subfields' );

        const normalize = array => array
            .sort( ({ order: a }, { order: b }) => a - b )
            // ignore "bindings" for subfields
            .map( json => Object.keys( json )
                .filter( key => key != 'bindings' && key != 'new-order' )
                .sort()
                .map( key => [key, json[key]] )
                .map( ([key, value]) => 'subfields' === key
                    ? [key, normalize( [...value] )]
                    : [key, value] )
                .reduce( (hash, [key, value]) => (hash[key] = value, hash), {} ) );

        const originalSubfields = normalize( oldSubfields );
        const fieldSubfields = normalize( newSubfields );

        if (JSON.stringify( originalSubfields ) !== JSON.stringify( fieldSubfields ))
            return diff.push( {
                attrName: 'subfields',
                oldValue: oldSubfields, newValue: newSubfields
            } ), diff;

        return diff;
    },

    suggestionOptions(text) {
        const url = Config.url( `/fields/${this.id}/values?search=${text}` );
        return Backbone.$.ajax( url )
            .then( ({ values }) => values.map( value => value[this.get( 'titleField' )] ) );
    }
    
}, {

    isBindingChanged(binding, originalModel) {
        if (!binding.id)
            return true;

        const [otherBinding] = originalModel.get( 'bindings' )
            .filter( otherBinding => otherBinding.id == binding.id );

        return JSON.stringify( normalizeBinding( binding ) ) !== JSON.stringify( normalizeBinding( otherBinding ) );
    },

    encodeString(decodedString) {
        return encodeURIComponent( decodedString.replaceAll( '.', '[dot]' ).replaceAll( "'", '[apo]' ) );
    },

    decodeString(encodedString) {
        return decodeURIComponent( encodedString ).replaceAll( '[dot]', '.' ).replaceAll( '[apo]', "'" );
    },

} );






function normalizeBinding({ id, type, on, visible_by_default, rules }) {
    rules = rules || [];
    if (!rules.length)
        rules = [...rules, { visible_by_default: true, visible_inverted_for_roles: [] }]

    return {
        id, type, on, visible_by_default, 
        rules: rules.map( ({ ...rule, visible_by_default, visible_inverted_for_roles }) => 
            ({ 
                ...rule, 
                visible_by_default: [true, 't'].includes( visible_by_default ),
                visible_inverted_for_roles: (visible_inverted_for_roles || []).map( id => +id ).sort()
            }) ),
    };
}


// WEBPACK FOOTER //
// ./src/js/model/refbooks/field.js